export default function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-amber-50 border-l-4 border-amber-400 p-6 mb-8">
          <div className="flex">
            <div className="ml-3">
              <p className="text-lg text-amber-700 font-semibold">
                For Healthcare Professionals Only
              </p>
              <p className="text-amber-600 mt-1">
                GPMeta is designed exclusively for qualified healthcare professionals.
                Not for patient use or clinical decision-making.
              </p>
            </div>
          </div>
        </div>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

        <div className="prose max-w-none">
          <p className="text-gray-600 mb-8">
            Compliant with Australian Privacy Act 1988 (Cth) and APP framework.
          </p>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Who We Are</h2>
            <p className="text-gray-700 leading-relaxed mb-2">
              Phronesis Health Pty Ltd (ABN: 52 658 820 449) operates GPMeta at gpmeta.au.
            </p>
            <p className="text-gray-700 leading-relaxed mb-2">Contact: hello@gpmeta.au</p>
            <p className="text-gray-700 leading-relaxed">Privacy Officer: Dr James Condon</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Account information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Account creation date</li>
              <li>Subscription status and billing history (via Stripe — we do not store card details)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Usage information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Queries submitted (text of questions asked)</li>
              <li>Responses received</li>
              <li>Sources accessed</li>
              <li>Session timestamps</li>
              <li>Mode used (Clinic / Study)</li>
              <li>Device type and browser</li>
              <li>IP address (for rate limiting and security)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Profile preferences</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>State and Local Health Network</li>
              <li>Source preferences</li>
              <li>Mode preference</li>
            </ul>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">What we do NOT collect</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Patient names, DOB, Medicare numbers, or any patient identifiers</li>
                <li>Clinical records</li>
                <li>AHPRA registration numbers</li>
                <li>Sensitive health information about you personally (unless you include it in a query)</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 mb-6">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Purpose</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Legal basis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Deliver the GPMeta service</td>
                    <td className="border border-gray-300 px-4 py-3">Contract performance</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Process subscription payments</td>
                    <td className="border border-gray-300 px-4 py-3">Contract performance</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Improve response quality (anonymised)</td>
                    <td className="border border-gray-300 px-4 py-3">Legitimate interest</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Send transactional emails (receipts, expiry notices)</td>
                    <td className="border border-gray-300 px-4 py-3">Contract performance</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Send product update emails</td>
                    <td className="border border-gray-300 px-4 py-3">Consent (opt-out available)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Detect and prevent abuse</td>
                    <td className="border border-gray-300 px-4 py-3">Legitimate interest</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">Comply with legal obligations</td>
                    <td className="border border-gray-300 px-4 py-3">Legal obligation</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Query Data</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Queries are processed by the Anthropic API (Claude) to generate responses.
              Anthropic's data processing terms apply: <a href="https://www.anthropic.com/legal/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://www.anthropic.com/legal/privacy</a>
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
              <p className="text-gray-700 leading-relaxed mb-2 font-semibold">We do not:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Link queries to patient records</li>
                <li>Share identifiable query content with third parties</li>
                <li>Use query content for advertising</li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-2 font-semibold">We may:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Review anonymised query patterns to improve source coverage and response quality</li>
                <li>Use aggregated, de-identified usage statistics for product development</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Who We Share Data With</h2>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 mb-6">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Party</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Purpose</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Anthropic</td>
                    <td className="border border-gray-300 px-4 py-3">Query processing (Claude API)</td>
                    <td className="border border-gray-300 px-4 py-3">USA</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">AWS</td>
                    <td className="border border-gray-300 px-4 py-3">Infrastructure hosting</td>
                    <td className="border border-gray-300 px-4 py-3">ap-southeast-2 (Sydney)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Stripe</td>
                    <td className="border border-gray-300 px-4 py-3">Payment processing</td>
                    <td className="border border-gray-300 px-4 py-3">USA</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">PostHog</td>
                    <td className="border border-gray-300 px-4 py-3">Usage analytics</td>
                    <td className="border border-gray-300 px-4 py-3">EU (self-hosted option available)</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">Cloudflare</td>
                    <td className="border border-gray-300 px-4 py-3">Bot protection, CDN</td>
                    <td className="border border-gray-300 px-4 py-3">Global</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-gray-700 leading-relaxed">
              All third parties are bound by data processing agreements.
              Where data is transferred overseas, appropriate safeguards apply under APP 8.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Storage</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Account and profile data: AWS DynamoDB, ap-southeast-2 (Sydney)</li>
              <li>Query logs: AWS, ap-southeast-2 (Sydney), retained 90 days then deleted</li>
              <li>Backups: AWS S3, ap-southeast-2 (Sydney)</li>
              <li>Payment data: Stripe (not stored by GPMeta)</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 mb-6">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Data type</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Retention period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Account data</td>
                    <td className="border border-gray-300 px-4 py-3">Duration of account + 7 years (tax/legal)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Query logs</td>
                    <td className="border border-gray-300 px-4 py-3">90 days</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="border border-gray-300 px-4 py-3">Billing records</td>
                    <td className="border border-gray-300 px-4 py-3">7 years (ATO requirement)</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">Inactive accounts</td>
                    <td className="border border-gray-300 px-4 py-3">Deleted after 2 years of inactivity with 30 days notice</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights (Australian Privacy Principles)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under the Privacy Act 1988 (Cth) you have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Access</strong> personal information we hold about you</li>
              <li><strong>Correct</strong> inaccurate personal information</li>
              <li><strong>Request deletion</strong> of your account and personal data</li>
              <li><strong>Opt out</strong> of marketing communications at any time</li>
              <li><strong>Complain</strong> to the Office of the Australian Information Commissioner (OAIC)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-2">
              To exercise any right: email hello@gpmeta.au<br />
              We will respond within 30 days.
            </p>
            <p className="text-gray-700">
              OAIC: <a href="https://www.oaic.gov.au" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://www.oaic.gov.au</a> | 1300 363 992
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We implement:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>HTTPS everywhere (TLS 1.2+)</li>
              <li>AWS Cognito for authentication (OAuth 2.0, PKCE)</li>
              <li>Secrets stored in AWS SSM Parameter Store (encrypted at rest)</li>
              <li>No passwords stored by GPMeta (delegated to Cognito / Google)</li>
              <li>IP-based rate limiting</li>
              <li>Cloudflare bot protection</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We cannot guarantee absolute security. In the event of a breach affecting
              your personal information, we will notify you and the OAIC as required
              under the Notifiable Data Breaches scheme.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Session cookies</strong> — required for login, cannot be disabled</li>
              <li><strong>PostHog analytics</strong> — usage patterns, anonymised. Can be opted out via settings.</li>
              <li><strong>Cloudflare Turnstile</strong> — bot detection on demo widget. No persistent tracking.</li>
            </ul>
            <p className="text-gray-700">
              We do not use advertising cookies or third-party tracking pixels.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children</h2>
            <p className="text-gray-700 leading-relaxed">
              GPMeta is not for use by persons under 18. We do not knowingly collect
              information from minors.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              Material changes notified by email 14 days before taking effect.
              Continued use constitutes acceptance.
            </p>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-12">
            <p className="text-sm text-gray-500 mb-2">
              Last updated: April 2026<br />
              Version: 1.0
            </p>
          </div>
        </div>

        <div className="mt-16 text-center">
          <a
            href="/"
            className="text-blue-600 hover:underline mr-8"
          >
            Back to GPMeta
          </a>
          <a
            href="/terms"
            className="text-blue-600 hover:underline"
          >
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  )
}